/**
 * Client-side calls to the feasibility engine routes. Plain fetch; every error
 * body is `{ error, code? }`, surfaced as an Error with that message.
 */
import type { PurposeTagLogDetail } from '@/lib/credits/tag-conversation-purpose';
import type { CheckConversation } from '@/lib/feasibility/conversations/store';
import type {
    CompareSurveyResponse,
    SimilarSurveysResponse,
} from '@/lib/feasibility/contracts/compare-survey';
import type {
    Draft,
    PastProject,
    PlanReadBack,
    ReportCheckRequest,
    ReportCheckResponse,
    ReportType,
} from '@/lib/feasibility/contracts/base-gaps';
import type { RerunInfo } from '@/lib/feasibility/check-extras';
import type { BriefInput, BriefResponse } from '@/lib/feasibility/contracts/brief';
import type {
    CheckSurveysResponse,
    FeedbackRequest,
    FeedbackResponse,
} from '@/lib/feasibility/contracts/feedback';
import type {
    StrategyPlanResponse,
    StrategyPrimerResponse,
} from '@/lib/feasibility/contracts/strategy';
import type { PublishPreview } from '@/lib/ani/publish-screens';
import type {
    AdLogCoverage,
    AdLogDay,
    ListKind,
    ReleaseGroupResponse,
    SavedList,
    SaveAdLogInput,
    SaveListInput,
    VisitPullRequest,
    VisitPullResponse,
    ZipLookupResponse,
} from '@/lib/feasibility/contracts/pql';
import type {
    IncidencePrediction,
    PastQuestionsResponse,
} from '@/lib/feasibility/contracts/incidence';
import type { DirectedShopReference } from '@/lib/feasibility/engine/directed-shop-reference';
import type { FeasibilityRequest, ScreenerQuestion } from '@/lib/feasibility/engine/request';
import type { FeatureKey, FeatureStatus } from '@/lib/feasibility/features';
import type { StudyTypeDef } from '@/lib/feasibility/engine/registry';
import type { SimpleCheckView } from '@/lib/feasibility/engine/project';
import type { SurveyRef } from '@/lib/feasibility/sources/survey-history';
import type { CheckRatingResponse, RatingReason } from '@/lib/feasibility/ratings/rating';

export const FEASIBILITY_API = '/api/studio/v1/feasibility';

export class FeasibilityApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string,
    ) {
        super(message);
    }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${FEASIBILITY_API}${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
        cache: 'no-store',
    });
    const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
        /** Set by a long answer that sent keep-alive bytes: the status it would have had (keep-alive.ts). */
        streamedStatus?: number;
    };
    if (!res.ok || body.streamedStatus)
        throw new FeasibilityApiError(
            body.error ?? `Request failed (${body.streamedStatus ?? res.status})`,
            body.streamedStatus ?? res.status,
            body.code,
        );
    return body as T;
}

export interface PoolSummary {
    poolKey: string;
    label: string;
    description: string;
    status: 'available' | 'pending_sourcing' | 'not_connected';
    criteriaKinds: string[];
    /** Where the pool is counted; the standard panel takes a last-login window. */
    sourceKind: string;
    eligibilityStatus: string;
    eligibilityFilter: string;
    owner: string | null;
    vendor: string | null;
}

export interface FeasibilityOptions {
    /** What is live or off (src/lib/feasibility/features.ts). */
    features: Record<FeatureKey, FeatureStatus>;
    studyTypes: StudyTypeDef[];
    pools: PoolSummary[];
    /** Answer labels per panel field: gender, state, dma, us_state_region and the profile fields. */
    quotaOptions: {
        gender: string[];
        state: string[];
        dma: string[];
        us_state_region: string[];
    } & Record<string, string[] | undefined>;
    quotaOptionsAvailable: boolean;
    intake: { source: 'plan_template' | 'built_in'; oneQuestion: string; updatedAt: string | null };
    /** The rate card's most people per release group (UAT UI-019); null when it is not set. */
    maxReleaseGroupSize: number | null;
    /** An out-of-home bid sent as zip codes: default miles around each zip (and in New York City). */
    oohResidentRadius?: { radiusMiles: number; nycRadiusMiles: number } | null;
}

export interface ParseResponse {
    request: FeasibilityRequest;
    provenance: Record<string, 'stated' | 'recommended'>;
    assumptions: string[];
    questions: string[];
    readBack: string[];
    template: { source: 'plan_template' | 'built_in'; name: string; updatedAt: string | null };
    model: string;
    elapsedMs: number;
    /** This reading, signed by the server; posted back with the confirmed request for the audit log. */
    receipt: string | null;
}

/** A history row: the simple view, plus the Quoted mark (A122) where it applies. */
export type HistoryRow = SimpleCheckView & {
    quoted?: { by: string; at: string } | null;
    /** A rerun's number and what changed (UAT UI-006). */
    rerun?: RerunInfo | null;
};

export const feasibilityApi = {
    options: () => call<FeasibilityOptions>('/options'),
    parse: (text: string, mode: 'describe' | 'one_question') =>
        call<ParseResponse>('/parse', { method: 'POST', body: JSON.stringify({ text, mode }) }),
    /** `daniReceipt`: the read-back's receipt when the request came from DANI (F-4.3). */
    /** `briefId`: the brief the request was read from (A95). */
    /** `rerunOf`: the check this one reruns (UAT UI-006). */
    start: (
        request: FeasibilityRequest,
        daniReceipt?: string | null,
        briefId?: string | null,
        rerunOf?: string | null,
    ) =>
        call<{ id: string }>('/checks', {
            method: 'POST',
            body: JSON.stringify({
                request,
                ...(daniReceipt ? { daniReceipt } : {}),
                ...(briefId ? { briefId } : {}),
                ...(rerunOf ? { rerunOf } : {}),
            }),
        }),
    /** A saved check as staff see it, for "Change and rerun". */
    check: (id: string) => call<SimpleCheckView>(`/checks/${encodeURIComponent(id)}?view=simple`),
    history: (params: {
        q?: string;
        scope?: 'mine' | 'all';
        limit?: number;
        offset?: number;
        quoted?: boolean;
        /** Only this person's checks (UAT UI-005). */
        by?: string;
    }) => {
        const sp = new URLSearchParams();
        if (params.q) sp.set('q', params.q);
        if (params.scope) sp.set('scope', params.scope);
        if (params.by) sp.set('by', params.by);
        if (params.quoted) sp.set('quoted', '1');
        sp.set('limit', String(params.limit ?? 20));
        sp.set('offset', String(params.offset ?? 0));
        return call<{ total: number; checks: HistoryRow[] }>(`/checks?${sp.toString()}`);
    },
    /** Everyone who has run a check, for History's "Run by" filter. */
    runners: () => call<{ people: { email: string; checks: number }[] }>('/checks?people=1'),
    /** Screener questions the panel has already answered (Amendment 10). */
    incidenceQuestions: (q: string) =>
        call<PastQuestionsResponse>(`/incidence/questions?q=${encodeURIComponent(q)}`),
    /** Incidence predicted from earlier answers to these screener questions. */
    predictIncidence: (screener: ScreenerQuestion[]) =>
        call<IncidencePrediction>('/incidence/predict', {
            method: 'POST',
            body: JSON.stringify({ screener }),
        }),
    names: (kind: 'app' | 'web' | 'store', q: string) =>
        call<{ items: { name: string; reach: number }[] }>(
            `/names?kind=${kind}&q=${encodeURIComponent(q)}`,
        ),
    brief: async (input: BriefInput) => {
        const read = () =>
            call<BriefResponse>('/brief', { method: 'POST', body: JSON.stringify(input) });
        try {
            return await read();
        } catch (err) {
            // A connection dropped on the way (not an answer from the tool): the read is safe to repeat.
            if (err instanceof TypeError) return read();
            throw err;
        }
    },
    /** DANI's rules for a "work it out with DANI" conversation, pre-seeded before the request. */
    strategyPrimer: () => call<StrategyPrimerResponse>('/strategy/primer'),
    /** DANI's `feasibility-plan` block as drafts, each with its signed reading. */
    strategyPlan: (plan: string, conversationId: string | null, forChat = false) =>
        call<StrategyPlanResponse>('/strategy/plan', {
            method: 'POST',
            body: JSON.stringify({ plan, conversationId, ...(forChat ? { forChat } : {}) }),
        }),
    /** How many panelists a visit pull would return, and the newest visit day loaded. */
    visitCount: (request: VisitPullRequest, signal?: AbortSignal) =>
        call<{ count: number; through: string | null }>('/visits/count', {
            method: 'POST',
            body: JSON.stringify(request),
            signal,
        }),
    /** The chosen categories' most-visited chains, for an in-store check (which takes chains). */
    categoryChains: (request: VisitPullRequest, slots: number, signal?: AbortSignal) =>
        call<{
            chains: { name: string; visitors: number }[];
            categoryVisitors: number;
            coveredVisitors: number;
        }>('/visits/category-chains', {
            method: 'POST',
            body: JSON.stringify({ request, slots }),
            signal,
        }),
    /** The phone brands and carriers on the panel now (M4S-5904). */
    deviceChoices: () => call<{ makes: string[]; carriers: string[] }>('/visits/device-choices'),
    visits: (input: VisitPullRequest) =>
        call<VisitPullResponse>('/visits', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    releaseGroup: (pullId: string, name: string, overrideReason?: string) =>
        call<ReleaseGroupResponse>('/visits/release-group', {
            method: 'POST',
            body: JSON.stringify({ pullId, name, ...(overrideReason ? { overrideReason } : {}) }),
        }),
    /**
     * Saved files. `summary: true` sends each file's counts in place of its
     * contents (BT-2223); load one file whole with `list(id)` before using its
     * places, screens or IDs.
     */
    lists: (
        filter?:
            | ListKind
            | { kind?: ListKind; orgId?: string; network?: string; q?: string; summary?: boolean },
    ) => {
        const { summary, ...f } =
            typeof filter === 'string' ? { kind: filter, summary: false } : (filter ?? {});
        const qs = new URLSearchParams([
            ...Object.entries(f).filter((e): e is [string, string] => Boolean(e[1])),
            ...(summary ? [['summary', '1'] as [string, string]] : []),
        ]).toString();
        return call<{ lists: SavedList[] }>(`/lists${qs ? `?${qs}` : ''}`);
    },
    list: (id: string) => call<{ list: SavedList }>(`/lists/${encodeURIComponent(id)}`),
    listVersions: (id: string) =>
        call<{ versions: SavedList[] }>(`/lists/${encodeURIComponent(id)}/versions`),
    adLogs: (listId: string) => call<AdLogCoverage>(`/lists/${encodeURIComponent(listId)}/ad-logs`),
    saveAdLog: (listId: string, input: SaveAdLogInput) =>
        call<{ day: AdLogDay; refusals: string[] }>(
            `/lists/${encodeURIComponent(listId)}/ad-logs`,
            {
                method: 'POST',
                body: JSON.stringify(input),
            },
        ),
    publishPreview: (listId: string) =>
        call<PublishPreview>(`/lists/${encodeURIComponent(listId)}/publish`),
    removeFromDani: (listId: string) =>
        call<{ removed: true }>(`/lists/${encodeURIComponent(listId)}/publish`, {
            method: 'DELETE',
        }),
    publish: (
        listId: string,
        input: { mode: 'append' | 'replace' | 'add'; includeUntagged: boolean },
    ) =>
        call<{ removes: number; inserted: number; alreadyThere: number; batch: string }>(
            `/lists/${encodeURIComponent(listId)}/publish`,
            { method: 'POST', body: JSON.stringify(input) },
        ),
    accounts: (q: string) =>
        call<{ accounts: { orgId: string; name: string }[] }>(
            `/accounts?q=${encodeURIComponent(q)}`,
        ),
    saveList: (input: SaveListInput, opts: { summary?: boolean } = {}) =>
        call<{ list: SavedList }>(`/lists${opts.summary ? '?summary=1' : ''}`, {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    patchList: (id: string, patch: { name?: string; used?: boolean }) =>
        call<{ list: SavedList }>(`/lists/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    deleteList: (id: string) =>
        call<{ ok: true }>(`/lists/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    zipLookup: (input: { listId?: string; coordinates?: { lat: number; lng: number }[] }) =>
        call<ZipLookupResponse>('/zip-lookup', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    reportTypes: () => call<{ types: ReportType[] }>('/reports/types'),
    reportCheck: (input: ReportCheckRequest) =>
        call<ReportCheckResponse>('/reports/checks', {
            method: 'POST',
            body: JSON.stringify(input),
        }),
    planTypes: () => call<{ types: ReportType[]; canWrite: boolean }>('/report-types'),
    readPlan: (markdown: string) =>
        call<PlanReadBack>('/report-types/parse', {
            method: 'POST',
            body: JSON.stringify({ markdown }),
        }),
    savePlan: (markdown: string, acceptRecommendedThreshold: boolean) =>
        call<{ type: ReportType }>('/report-types', {
            method: 'POST',
            body: JSON.stringify({ markdown, acceptRecommendedThreshold }),
        }),
    surveys: (q: string) => call<{ surveys: SurveyRef[] }>(`/surveys?q=${encodeURIComponent(q)}`),
    surveysById: (ids: number[]) => call<{ surveys: SurveyRef[] }>(`/surveys?ids=${ids.join(',')}`),
    similarProjects: (q: string, studyType?: string) =>
        call<{ projects: PastProject[] }>(
            `/projects/similar?q=${encodeURIComponent(q)}${studyType ? `&studyType=${encodeURIComponent(studyType)}` : ''}`,
        ),
    drafts: () => call<{ drafts: Draft[] }>('/drafts'),
    saveDraft: (request: Record<string, unknown>, id?: string) =>
        call<{ draft: Draft }>('/drafts', {
            method: 'POST',
            body: JSON.stringify({ request, ...(id ? { id } : {}) }),
        }),
    getDraft: (id: string) => call<{ draft: Draft }>(`/drafts/${encodeURIComponent(id)}`),
    /** Removes a check from History (hidden, not deleted); only its owner or an admin may. */
    hideCheck: (id: string) =>
        call<{ ok: true }>(`/checks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    deleteDraft: (id: string) =>
        call<{ ok: true }>(`/drafts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    /** Chains as the visit data names them, with visitors in the window (the pull's own rules). */
    /** Panel clients whose name contains the text, most past surveys first. */
    /** What past studies like this qualified, offered when nobody gave the share (past-share.ts). */
    pastShare: (studyType: string, client: string | null) =>
        call<{
            share: {
                value: number;
                low: number;
                high: number;
                studies: number;
                from: 'client' | 'recent';
            } | null;
            sentence: string | null;
        }>(`/past-share?${new URLSearchParams({ studyType, ...(client ? { client } : {}) })}`),
    clients: (q: string) =>
        call<{ clients: { name: string; surveys: number; lastSurvey: string | null }[] }>(
            `/clients?${new URLSearchParams({ q })}`,
        ),
    visitChains: (
        q: string,
        w: { from: string; to: string; dwell: number; confidence: string; usOnly: boolean },
    ) =>
        call<{ chains: { name: string; visitors: number }[] }>(
            `/visits/chains?${new URLSearchParams({
                q,
                from: w.from,
                to: w.to,
                dwell: String(w.dwell),
                confidence: w.confidence,
                usOnly: String(w.usOnly),
            })}`,
        ),
    chains: (q: string) =>
        call<{
            items: { chainId: string; name: string; locationCount: number; categories: string[] }[];
        }>(`/chains?q=${encodeURIComponent(q)}`),
    /** A check against a past survey: the survey's facts and a rerun on its inputs. */
    compareSurvey: (checkId: string, survey: string | number) =>
        call<CompareSurveyResponse>(`/checks/${encodeURIComponent(checkId)}/compare-survey`, {
            method: 'POST',
            body: JSON.stringify({ survey }),
        }),
    /** The DANI conversations about a check, with the copy a quote keeps. */
    checkConversations: (checkId: string) =>
        call<{ conversations: (CheckConversation & { mine?: boolean })[] }>(
            `/checks/${encodeURIComponent(checkId)}/conversations`,
        ),
    /** The check a DANI conversation is about, so the chat keeps it in view. */
    conversationCheck: (conversationId: string) =>
        call<{ checkId: string }>(`/conversations/${encodeURIComponent(conversationId)}/check`),
    /**
     * Links a DANI conversation the caller is working in to a check. `creditTag`
     * is how the credits purpose tag went on a conversation the check started,
     * logged on the check (M4S-5912).
     */
    linkConversation: (
        checkId: string,
        conversationId: string,
        purpose: 'ran' | 'explained' | 'compared',
        creditTag?: PurposeTagLogDetail | null,
    ) =>
        call<{ ok: true }>(`/checks/${encodeURIComponent(checkId)}/conversations`, {
            method: 'POST',
            body: JSON.stringify({ conversationId, purpose, ...(creditTag ? { creditTag } : {}) }),
        }),
    /** Past surveys like a check, with what each did. */
    similarSurveys: (checkId: string) =>
        call<SimilarSurveysResponse>(`/checks/${encodeURIComponent(checkId)}/similar-surveys`, {
            method: 'POST',
        }),
    /** Past directed shops nearest a directed-shop check. */
    directedShops: (checkId: string) =>
        call<DirectedShopReference | { loading: true }>(
            `/checks/${encodeURIComponent(checkId)}/directed-shops`,
        ),
    checkSurveys: (checkId: string) =>
        call<CheckSurveysResponse>(`/checks/${encodeURIComponent(checkId)}/surveys`),
    linkSurvey: (checkId: string, body: { surveyId: number } | { find: true }) =>
        call<CheckSurveysResponse>(`/checks/${encodeURIComponent(checkId)}/surveys`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    unlinkSurvey: (checkId: string, surveyId: number) =>
        call<CheckSurveysResponse>(
            `/checks/${encodeURIComponent(checkId)}/surveys?surveyId=${surveyId}`,
            { method: 'DELETE' },
        ),
    /** Was this estimate useful? The caller's own rating and the counts. */
    checkRating: (checkId: string) =>
        call<CheckRatingResponse>(`/checks/${encodeURIComponent(checkId)}/rating`),
    rateCheck: (
        checkId: string,
        body: { rating: 'up' | 'down'; reasons?: RatingReason[]; note?: string },
    ) =>
        call<CheckRatingResponse>(`/checks/${encodeURIComponent(checkId)}/rating`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    feedback: (body: FeedbackRequest) =>
        call<FeedbackResponse>('/feedback', { method: 'POST', body: JSON.stringify(body) }),
};
